/**
 * The platform's own status line: how many client accounts trade and whether
 * the exchange link of each is confirmed.
 *
 * It replaces the account freshness row on the platform, which has no single
 * exchange link of its own; that row read "accounts not connected, trading
 * off" while every account was trading.
 */
import { useClientOperations } from "@/shared/clients/operations";
import { PROOF_FRESH_SECONDS } from "@/shared/clients/roster";
import { plural } from "@/shared/text/plural";

export function PlatformPulse(): React.JSX.Element {
  const query = useClientOperations();
  let dot = "bg-text-muted";
  let text = "Проверяю счета…";
  if (query.isError) {
    dot = "bg-status-warning";
    text = "Состояние счетов неизвестно — сводка недоступна";
  } else if (query.data !== undefined) {
    const accounts = query.data;
    const trading = accounts.filter((item) => item.trading).length;
    const lost = accounts.filter(
      (item) => item.bybit_connected && (item.proof_age_seconds === null || item.proof_age_seconds > PROOF_FRESH_SECONDS),
    );
    const total = accounts.length;
    const head = total === 0
      ? "Счетов пока нет"
      : `Торгуют ${String(trading)} из ${String(total)} ${plural(total, "счёта", "счетов", "счетов")}`;
    if (lost.length > 0) {
      dot = "bg-status-negative";
      text = `${head} · нет связи с биржей: ${lost.map((item) => item.display_name).join(", ")}`;
    } else {
      dot = total === 0 ? "bg-text-muted" : "bg-status-positive";
      text = total === 0 ? head : `${head} · связь с биржей в порядке`;
    }
  }
  return (
    <div className="flex min-h-7 items-center bg-background-base px-4 py-1 shell:h-7 shell:py-0">
      <span className="flex min-w-0 items-center gap-2 text-helper-sm text-text-secondary" data-testid="connection-state" role="status">
        <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${dot}`} />
        {text}
      </span>
    </div>
  );
}
